<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { type Task, taskApi } from '@/api/entities/review/task'

definePage({
  meta: {
    title: '작업 상태',
    action: 'read',
    subject: 'review',
  },
})

const { snackbar, handleError } = useErrorHandler()

// 상태
const loading = ref(false)
const tasks = ref<Task[]>([])
const confirmDialog = ref(false)
const taskToDelete = ref<string | null>(null)

// 테이블 헤더
const headers = [
  { title: '고객사', key: 'client_name', sortable: true },
  { title: '채널', key: 'driver_name', sortable: true },
  { title: '역할', key: 'type', sortable: true },
  { title: '상태', key: 'status', sortable: true },
  { title: '', key: 'action', sortable: false, width: '100px' },
]

// 데이터 로드
const loadTasks = async () => {
  loading.value = true
  try {
    const { success, data } = await taskApi.fetchTasks()

    if (success && data)
      tasks.value = data || []
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loading.value = false
  }
}

// 태스크 삭제
const confirmDeleteTask = (taskId: string) => {
  taskToDelete.value = taskId
  confirmDialog.value = true
}

const deleteTask = async () => {
  if (!taskToDelete.value)
    return

  try {
    const { success } = await taskApi.deleteTask(taskToDelete.value)

    if (success) {
      snackbar.success('태스크가 취소되었습니다.')
      loadTasks()
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '태스크 취소에 실패했습니다.' })
  }
  finally {
    confirmDialog.value = false
    taskToDelete.value = null
  }
}

// 상태에 따른 색상
const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'secondary'
    case 'started':
      return 'success'
    case 'failed':
      return 'error'
    default:
      return 'info'
  }
}

// 상태 텍스트
const getStatusText = (status: string, statusText?: string) => {
  switch (status) {
    case 'pending':
      return '대기 중'
    case 'started':
      return '진행 중'
    default:
      return statusText || status
  }
}

// 역할 아이콘
const getTypeIcon = (type: string) => {
  return type === 'up' ? 'tabler-arrow-up-circle' : 'tabler-arrow-down-circle'
}

const getTypeColor = (type: string) => {
  return type === 'up' ? 'primary' : 'info'
}

onMounted(() => {
  loadTasks()
})
</script>

<template>
  <div>
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center">
        작업 상태
        <VBtn
          icon="tabler-refresh"
          variant="text"
          size="small"
          @click="loadTasks"
        />
      </VCardTitle>
      <VCardText>
        <VDataTable
          :headers="headers"
          :items="tasks"
          :loading="loading"
          class="elevation-0"
        >
          <!-- 고객사 -->
          <template #item.client_name="{ item }">
            <div>
              <strong>{{ item.client_name }}</strong>
              <div class="text-caption text-medium-emphasis">
                {{ item.client_id }}
              </div>
            </div>
          </template>

          <!-- 채널 -->
          <template #item.driver_name="{ item }">
            <div>
              <strong>{{ item.driver_name }}</strong>
              <div class="text-caption text-medium-emphasis">
                {{ item.driver_config_id }}
              </div>
            </div>
          </template>

          <!-- 역할 -->
          <template #item.type="{ item }">
            <div class="d-flex align-center gap-2">
              <VIcon
                :icon="getTypeIcon(item.type)"
                :color="getTypeColor(item.type)"
                size="24"
              />
              <span>{{ item.type_text }}</span>
            </div>
          </template>

          <!-- 상태 -->
          <template #item.status="{ item }">
            <VChip
              :color="getStatusColor(item.status)"
              size="small"
            >
              {{ getStatusText(item.status, item.status_text) }}
            </VChip>
          </template>

          <!-- 작업 -->
          <template #item.action="{ item }">
            <VBtn
              variant="outlined"
              color="error"
              size="small"
              @click="confirmDeleteTask(item.task_id)"
            >
              취소
            </VBtn>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="400"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>해당 태스크를 취소하시겠습니까?</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            아니오
          </VBtn>
          <VBtn
            color="error"
            @click="deleteTask"
          >
            예, 취소합니다
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </div>
</template>
